import React from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { X, ArrowRight, Hourglass, Zap } from 'lucide-react';
import { NoteGrid, fmtDT, fmtTime, REASON_LABEL, minutesLabel } from './NoteGrid';

export const NowShowingCard = ({ status, onChanged }) => {
  const cur = status?.current;
  const dry = status?.settings?.dry_run;
  const cancelManual = async () => {
    try { await api.vbManagerCancelManual(); toast.success('Manueller Push beendet — Zeitplan läuft weiter'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  const dropEvent = async (id) => {
    try { await api.vbManagerDropEvent(id); toast.success('Event verworfen'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="vb-now-showing">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Jetzt auf der Note</p>
          <p className="font-heading text-lg text-foreground mt-0.5" data-testid="vb-now-board">
            {cur ? cur.board_name : 'Noch nichts gesendet'}
            {cur && <span className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">{REASON_LABEL[cur.reason] || cur.reason}</span>}
          </p>
          {cur && (
            <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="vb-now-since">
              seit {fmtDT(cur.since)}{cur.until ? ` · bis ${fmtTime(cur.until)}` : ' · bis erledigt'}
              {cur.frames > 1 && ` · Frame ${cur.frame_index + 1}/${cur.frames}`}{cur.by && ` · von ${cur.by}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!status?.in_office_hours && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/10" data-testid="vb-outside-office">außerhalb Bürozeit</span>}
          {status?.settings?.paused && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-red-500/40 text-red-300 bg-red-500/10">PAUSE</span>}
          {status?.settings?.visitor_mode && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-sky-500/40 text-sky-300 bg-sky-500/10">BESUCHER-MODUS</span>}
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${dry ? 'text-amber-300 border-amber-500/40 bg-amber-500/10' : 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'}`} data-testid="vb-mode-badge">{dry ? 'DRY_RUN' : 'LIVE'}</span>
        </div>
      </div>
      <div className="flex justify-center my-5">
        <NoteGrid matrix={cur?.matrix} size="lg" testid="vb-now-grid" />
      </div>
      {status?.manual && (
        <div className="flex items-center justify-between gap-2 text-xs border border-amber-500/30 bg-amber-500/5 rounded-sm px-3 py-2 mb-3" data-testid="vb-manual-active">
          <span className="text-amber-200"><Zap className="w-3.5 h-3.5 inline mr-1" />Manueller Push aktiv bis {fmtTime(status.manual.until)} ({status.manual.by})</span>
          <Button size="sm" variant="ghost" onClick={cancelManual} className="h-6 text-[11px] text-muted-foreground hover:text-foreground" data-testid="vb-cancel-manual-btn"><X className="w-3 h-3 mr-1" /> Beenden</Button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3 text-xs">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Als Nächstes (Rotation)</p>
          {status?.next_rotation ? (
            <p className="text-foreground" data-testid="vb-next-rotation"><ArrowRight className="w-3 h-3 inline mr-1 text-muted-foreground" />{status.next_rotation.board_name} <span className="text-muted-foreground">· {minutesLabel(status.next_rotation.display_seconds)}</span></p>
          ) : <p className="text-muted-foreground">Kein aktives Rotation-Board</p>}
          {status?.rotation_order?.length > 1 && (
            <p className="text-[10px] text-muted-foreground mt-1">Reihenfolge: {status.rotation_order.map(r => r.board_name).join(' → ')}</p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Event-Warteschlange · {status?.queued_events?.length || 0}</p>
          {(status?.queued_events || []).length === 0 ? <p className="text-muted-foreground">Keine Events offen</p> : (
            <div className="space-y-1" data-testid="vb-queue">
              {status.queued_events.map(ev => (
                <div key={ev.id} className="flex items-center justify-between gap-2" data-testid={`vb-queue-${ev.id}`}>
                  <span className="text-foreground truncate">
                    <span className="font-mono text-[10px] text-muted-foreground mr-1">P{ev.priority}</span>{ev.board_name}
                    {ev.summary && <span className="text-muted-foreground"> · {ev.summary}</span>}
                    <span className={`ml-1 text-[10px] ${ev.status === 'showing' ? 'text-emerald-400' : ev.blocked ? 'text-amber-400' : 'text-muted-foreground'}`}>
                      {ev.status === 'showing' ? 'läuft' : ev.blocked ? 'wartet' : 'bereit'}
                    </span>
                  </span>
                  <button onClick={() => dropEvent(ev.id)} className="text-muted-foreground hover:text-red-400" title="Verwerfen" data-testid={`vb-drop-event-${ev.id}`}><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {status?.state?.last_tick_at && (
        <p className="text-[10px] text-muted-foreground mt-3 flex items-center gap-1"><Hourglass className="w-3 h-3" /> letzter Takt {fmtTime(status.state.last_tick_at)} ({status.state.last_tick_status}) · letzter Versand {fmtDT(status.state.last_sent_at)}</p>
      )}
    </div>
  );
};
